import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface InboxNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  channel: string;
  status: string;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

export interface InboxFilters {
  page?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  status?: string; // 'read' | 'unread' | ''
  channel?: string; // 'in_app' | 'email' | ''
}

interface InboxResponse {
  success: boolean;
  data: InboxNotification[];
  unreadCount: number;
  availableTypes: string[];
  pagination: { page: number; pageSize: number; total: number };
}

const inboxKeys = {
  all: ['notifications-inbox'] as const,
  list: (filters: InboxFilters) => [...inboxKeys.all, filters] as const,
};

export function useNotificationsInbox(filters: InboxFilters = {}) {
  return useQuery({
    queryKey: inboxKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page || 1));
      params.set('pageSize', '50');
      if (filters.search) params.set('search', filters.search);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.type) params.set('type', filters.type);
      if (filters.status) params.set('status', filters.status);
      if (filters.channel !== undefined) params.set('channel', filters.channel);

      const response = await apiClient.get<InboxResponse>(
        `/notifications/inbox?${params.toString()}`,
      );
      return response.data;
    },
    refetchInterval: 60000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.patch(`/notifications/inbox/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/notifications/inbox/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });
}
